import * as THREE from 'three'
import { RunConfig } from '../../config/runs'
import { getElevation } from '../../world/terrain/SlopeTerrain'

/**
 * Slopestyle jump ramps — big snow wedges with blue edge trim.
 * Simple extruded wedge shape that's solid and visible.
 */

function seededRandom(seed: number): number {
  const n = Math.sin(seed * 127.1) * 43758.5453
  return n - Math.floor(n)
}

type RampSize = 'small' | 'medium' | 'large'

const RAMP_CONFIGS: Record<RampSize, { width: number; length: number; height: number }> = {
  small:  { width: 6,  length: 5,  height: 1.5 },
  medium: { width: 10, length: 8,  height: 3.0 },
  large:  { width: 14, length: 12, height: 5.0 },
}

function createRamp(size: RampSize): THREE.Group {
  const cfg = RAMP_CONFIGS[size]
  const ramp = new THREE.Group()

  const snowMat = new THREE.MeshStandardMaterial({ color: 0xb8ccdf, roughness: 0.25, side: THREE.DoubleSide })
  const blueMat = new THREE.MeshStandardMaterial({ color: 0x0077ff, roughness: 0.25 })

  // Build the wedge from simple boxes — guaranteed solid and visible
  // Skier approaches from z=0 (base), lip is at z=-length

  // Angled top surface (the slope you ski up)
  const slopeLen = Math.sqrt(cfg.length ** 2 + cfg.height ** 2)
  const slopeAngle = Math.atan2(cfg.height, cfg.length)
  const topSlab = new THREE.Mesh(
    new THREE.BoxGeometry(cfg.width, 1.0, slopeLen),
    snowMat
  )
  topSlab.rotation.x = slopeAngle
  topSlab.position.set(0, cfg.height / 2, -cfg.length / 2)
  topSlab.castShadow = true
  topSlab.receiveShadow = true
  ramp.add(topSlab)

  // Vertical lip face (tall wall at the end)
  const lipFace = new THREE.Mesh(
    new THREE.BoxGeometry(cfg.width, cfg.height, 0.5),
    snowMat
  )
  lipFace.position.set(0, cfg.height / 2, -cfg.length)
  lipFace.castShadow = true
  ramp.add(lipFace)

  // Bottom slab (ground level, fills the base)
  const bottomSlab = new THREE.Mesh(
    new THREE.BoxGeometry(cfg.width, 0.3, cfg.length),
    snowMat
  )
  bottomSlab.position.set(0, 0.15, -cfg.length / 2)
  ramp.add(bottomSlab)

  // Fill blocks underneath the slope (stack of boxes to make it solid)
  const fillSteps = 4
  for (let i = 0; i < fillSteps; i++) {
    const t = (i + 1) / fillSteps
    const fillHeight = cfg.height * t * 0.8
    const fillLength = cfg.length * t
    const fillBlock = new THREE.Mesh(
      new THREE.BoxGeometry(cfg.width - 0.1, fillHeight, fillLength * 0.25),
      snowMat
    )
    fillBlock.position.set(0, fillHeight / 2, -cfg.length + fillLength * 0.125)
    ramp.add(fillBlock)
  }

  // === BLUE EDGE TRIM ===

  // === BLUE EDGE TRIM ===
  // Base at z=0, lip at z=-length, height at lip = cfg.height

  // Top lip bar
  const lipBar = new THREE.Mesh(
    new THREE.BoxGeometry(cfg.width + 0.4, 0.3, 0.3),
    blueMat
  )
  lipBar.position.set(0, cfg.height + 0.15, -cfg.length)
  ramp.add(lipBar)

  // Bottom base bar
  const baseBar = new THREE.Mesh(
    new THREE.BoxGeometry(cfg.width + 0.4, 0.2, 0.2),
    blueMat
  )
  baseBar.position.set(0, 0.1, 0.1)
  ramp.add(baseBar)

  // Bottom lip bar
  const lipBottomBar = new THREE.Mesh(
    new THREE.BoxGeometry(cfg.width + 0.4, 0.2, 0.2),
    blueMat
  )
  lipBottomBar.position.set(0, 0.1, -cfg.length)
  ramp.add(lipBottomBar)

  // Slope edges (left and right along the angled top)
  const edgeLen = Math.sqrt(cfg.length ** 2 + cfg.height ** 2)
  const edgeAngle = Math.atan2(cfg.height, cfg.length)
  for (const side of [-1, 1]) {
    const slopeEdge = new THREE.Mesh(
      new THREE.BoxGeometry(0.22, 0.22, edgeLen + 0.2),
      blueMat
    )
    slopeEdge.rotation.x = edgeAngle
    slopeEdge.position.set(side * (cfg.width / 2 + 0.1), cfg.height / 2, -cfg.length / 2)
    ramp.add(slopeEdge)
  }

  // Vertical lip edges (corners of the lip face)
  for (const side of [-1, 1]) {
    const vertEdge = new THREE.Mesh(
      new THREE.BoxGeometry(0.22, cfg.height + 0.3, 0.22),
      blueMat
    )
    vertEdge.position.set(side * (cfg.width / 2 + 0.1), cfg.height / 2, -cfg.length)
    ramp.add(vertEdge)
  }

  // Center stripe up the slope
  const centerStripe = new THREE.Mesh(
    new THREE.BoxGeometry(0.18, 0.18, edgeLen + 0.2),
    blueMat
  )
  centerStripe.rotation.x = edgeAngle
  centerStripe.position.set(0, cfg.height / 2 + 0.25, -cfg.length / 2)
  ramp.add(centerStripe)

  return ramp
}

export interface RampData {
  mesh: THREE.Group
  position: THREE.Vector3
  width: number
  depth: number
  size: RampSize
  launchVelocity: number
}

export function createJumpRamps(config: RunConfig): RampData[] {
  if (!config.jumps) return []

  const ramps: RampData[] = []
  const rampCount = Math.floor(config.length / 50)

  for (let i = 0; i < rampCount; i++) {
    const seed = i * 13.7

    const sizeRoll = seededRandom(seed + 10)
    let size: RampSize
    if (config.difficulty === 'green') {
      size = sizeRoll < 0.7 ? 'small' : 'medium'
    } else if (config.difficulty === 'blue') {
      size = sizeRoll < 0.3 ? 'small' : sizeRoll < 0.8 ? 'medium' : 'large'
    } else {
      size = sizeRoll < 0.15 ? 'small' : sizeRoll < 0.5 ? 'medium' : 'large'
    }

    const cfg = RAMP_CONFIGS[size]
    const x = (seededRandom(seed) - 0.5) * (config.width * 0.5)
    const z = -config.length / 2 + 40 + (i / rampCount) * (config.length * 0.8)
    const groundY = getElevation(x, z)

    const ramp = createRamp(size)
    ramp.position.set(x, groundY, z)

    const launchMap: Record<RampSize, number> = {
      small: 3,
      medium: 5,
      large: 7,
    }

    ramps.push({
      mesh: ramp,
      position: new THREE.Vector3(x, groundY, z),
      width: cfg.width,
      depth: cfg.length,
      size,
      launchVelocity: launchMap[size],
    })
  }

  return ramps
}
